import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { activityLogService } from '../../services/compliance/activityLogService';
import { ACTIVITY_LOG_ACTIONS } from '../../types/compliance';
// import { Button } from '../../components/ui/Button';
import { motion } from 'framer-motion';
import ChartContainer from '../../components/coach/charts/common/ChartContainer';
import { ComponentErrorBoundary } from '../../components/ErrorBoundary/ComponentErrorBoundary';
import SessionParametersPanel from '../../components/coach/dashboard/SessionParametersPanel';
import SessionRosterPanel from '../../components/coach/dashboard/SessionRosterPanel';
import OverviewMetricCardsWithHullFilter from '../../components/coach/dashboard/OverviewMetricCardsWithHullFilter';
import HullStabilityPanel from '../../components/coach/dashboard/HullStabilityPanel';
import { HullStabilityFilterProvider } from '../../contexts/HullStabilityFilterContext';
import CompactHeader from '../../components/coach/dashboard/CompactHeader';
import OverviewCards from '../../components/coach/dashboard/OverviewCards';
import WelcomeModal from '../../components/coach/dashboard/WelcomeModal';
// import TechniqueAnalysisCards from '../../components/coach/TechniqueAnalysisCards';

import {
  useSessionInfo,
  useSessionsList,
  usePowerData,
  useTelemetryData,
  useAveragePowerData,
  useComprehensiveData,
  useStrokeRateData,
  useEfficiencyData,
} from '../../hooks/useSessionData';
import { useSessionParameters } from '../../hooks/useChartData';
import { useCoachPreferences } from '../../hooks/useCoachPreferences';
import { GraphWidget } from '../../platform/graph-registry';
import { useActiveTeamId } from '../../contexts/ActiveTeamContext';
import CoachDashboardNavBar from '../../components/coach/dashboard/CoachDashboardNavBar';
import {
  clearUploadBatch,
  getActiveUploadBatch,
  mergeUploadBatchFromLocationState,
  navigateToBatchSession,
  type UploadBatchContext,
  type UploadBatchLocationState,
} from '../../utils/upload/uploadBatchNavigation';
import {
  clearTeamDashboardContext,
  getActiveTeamDashboardContext,
  mergeTeamDashboardFromLocationState,
  type TeamDashboardContext,
  type TeamDashboardLocationState,
} from '../../utils/coach/teamDashboardNavigation';

// Lazy load heavy chart components that are used conditionally
const GPSTrackMap = React.lazy(() => import('../../components/coach/dashboard/GPSTrackMap'));

interface CoachDashboardProps {
  selectedSessionId?: string | null;
}

// Loading skeleton for charts
const ChartSkeleton: React.FC = () => (
  <div className="animate-pulse bg-gray-100 rounded-lg h-96 flex items-center justify-center">
    <div className="text-gray-400 text-sm">Loading chart...</div>
  </div>
);

type DashboardHashTarget =
  | 'power'
  | 'overview'
  | 'individual-rower-power'
  | 'force'
  | 'arc'
  | 'gps'
  | 'map';

const DASHBOARD_HASH_TARGETS: DashboardHashTarget[] = [
  'power',
  'overview',
  'individual-rower-power',
  'force',
  'arc',
  'gps',
  'map',
];

const isDashboardHashTarget = (value: string): value is DashboardHashTarget =>
  DASHBOARD_HASH_TARGETS.includes(value as DashboardHashTarget);

const mapHashToSectionId = (hashValue: string): string | null => {
  const hash = hashValue.replace('#', '').toLowerCase();
  if (hash === 'power') return 'dashboard-power';
  if (hash === 'overview') return 'dashboard-overview';
  if (
    hash === 'individual-rower-power' ||
    hash === 'individualrowerpower' ||
    hash === 'individual-power'
  ) {
    return 'dashboard-individual-rower-power';
  }
  if (hash === 'force' || hash === 'gate-force' || hash === 'gateforce') {
    return 'dashboard-gate-force';
  }
  if (hash === 'arc' || hash === 'rowing-arc' || hash === 'rowingarc') {
    return 'dashboard-rowing-arc';
  }
  // Bookmarks / older links
  if (hash === 'technique') {
    return 'dashboard-gate-force';
  }
  if (hash === 'analysis') return 'dashboard-analysis';
  if (hash === 'gps' || hash === 'map') return 'dashboard-map';
  return null;
};

const CoachDashboard: React.FC<CoachDashboardProps> = ({ selectedSessionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const activeTeamId = useActiveTeamId();

  const uploadBatch = userId
    ? mergeUploadBatchFromLocationState(location.state as UploadBatchLocationState, userId)
    : null;
  const teamDashboardContext = userId
    ? mergeTeamDashboardFromLocationState(location.state as TeamDashboardLocationState, userId)
    : null;
  const [dismissedBatchAt, setDismissedBatchAt] = useState<number | null>(null);

  // React Query hooks for cached data fetching
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [overviewExpandSignal, setOverviewExpandSignal] = useState<number>(0);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

  // Session data hooks
  const { data: sessionInfo } = useSessionInfo(sessionId);
  const { data: sessions, isLoading: sessionsLoading } = useSessionsList();
  const { preferences: coachPreferences, updatePreferences } = useCoachPreferences();
  const _hasSessions = sessions && sessions.length > 0;

  // Filter sessions based on showDemoContent preference (active sessions only from list query)
  const filteredSessions = useMemo(() => {
    if (!sessions) return [];
    let list = coachPreferences.showDemoContent
      ? sessions
      : sessions.filter((session) => !session.isDemo);
    // Allow viewing an archived session when it is explicitly selected (useSessionInfo resolves it)
    if (
      sessionId &&
      sessionInfo &&
      sessionInfo.id === sessionId &&
      !list.some((s) => s.id === sessionId)
    ) {
      list = [sessionInfo, ...list];
    }
    return list;
  }, [sessions, coachPreferences.showDemoContent, sessionId, sessionInfo]);

  // Check if user has any real (non-demo) sessions
  const hasUserSessions = useMemo(() => {
    if (!sessions) return false;
    return sessions.some((session) => !session.isDemo);
  }, [sessions]);

  // FERPA Activity Logging: Log when coach views session data
  useEffect(() => {
    const logAccess = async () => {
      if (!sessionId || !sessionInfo || !user) return;

      // Skip logging for demo sessions
      if (sessionInfo.isDemo) return;

      await activityLogService.log({
        action_type: ACTIVITY_LOG_ACTIONS.VIEW_SESSION_DATA,
        resource_type: 'session',
        resource_id: sessionId,
        metadata: {
          session_name: sessionInfo.sessionName,
          team_id: activeTeamId,
          session_date: sessionInfo.sessionDate,
          endpoint: '/coach/dashboard',
        },
      });
    };

    logAccess(); // Fire-and-forget, non-blocking
  }, [sessionId, sessionInfo, user, activeTeamId]);

  // Chart data hooks
  const { data: powerData, isLoading: powerLoading } = usePowerData(sessionId);
  const { data: _telemetryData, isLoading: telemetryLoading } = useTelemetryData(sessionId);
  const {
    data: averagePowerData,
    isLoading: averagePowerLoading,
    error: averagePowerError,
  } = useAveragePowerData(sessionId);
  const { data: comprehensiveData, isLoading: comprehensiveLoading } =
    useComprehensiveData(sessionId);
  const { data: parameterData } = useSessionParameters(sessionId);

  // Performance metrics hooks
  const { data: strokeRateData, isLoading: strokeRateLoading } = useStrokeRateData(sessionId);
  const { data: efficiencyData, isLoading: efficiencyLoading } = useEfficiencyData(sessionId);

  // Defensive checks to prevent crashes during loading
  const _isDataLoading =
    powerLoading || telemetryLoading || averagePowerLoading || comprehensiveLoading;
  const hasPowerData = powerData && Array.isArray(powerData) && powerData.length > 0;

  // Extract values from data objects
  const strokeRate = strokeRateData?.strokeRate || 0;
  const efficiency = efficiencyData?.efficiency || 0;

  // Transform powerData to match PowerDataPoint interface for OverviewCards
  const transformedPowerData = hasPowerData
    ? powerData!.map((point, index) => ({
        interval: `Interval ${index + 1}`,
        ...point,
      }))
    : [];

  // Auto-resurface demo content if user has no real sessions and demo is hidden
  useEffect(() => {
    if (!coachPreferences.showDemoContent && !hasUserSessions && sessions && sessions.length > 0) {
      // User has no real sessions but demo is hidden - automatically show demo again
      updatePreferences({ showDemoContent: true }).catch((err) => {
        console.error('Failed to auto-resurface demo content:', err);
      });
    }
  }, [coachPreferences.showDemoContent, hasUserSessions, sessions, updatePreferences]);

  // Handle session selection with React Query (using filtered sessions)
  useEffect(() => {
    if (selectedSessionId) {
      // Trust explicit selection (URL / post-upload link) even when the sessions list
      // cache has not refetched yet.
      setSessionId(selectedSessionId);
      return;
    }

    if (filteredSessions && filteredSessions.length > 0) {
      // Auto-select non-demo sessions first, then demo sessions
      const nonDemoSessions = filteredSessions.filter((session) => !session.isDemo);
      const demoSessions = filteredSessions.filter((session) => session.isDemo);

      if (nonDemoSessions.length > 0) {
        setSessionId(nonDemoSessions[0].id);
      } else if (demoSessions.length > 0) {
        // Auto-load demo session if no user sessions exist
        setSessionId(demoSessions[0].id);
      }
    }
  }, [selectedSessionId, filteredSessions]);

  const effectiveSessionId = selectedSessionId ?? sessionId;
  const activeUploadBatch = useMemo((): UploadBatchContext | null => {
    const batch = getActiveUploadBatch(uploadBatch, effectiveSessionId);
    if (!batch) return null;
    if (dismissedBatchAt === batch.createdAt) return null;
    return batch;
  }, [uploadBatch, effectiveSessionId, dismissedBatchAt]);

  const activeTeamDashboard = useMemo((): TeamDashboardContext | null => {
    if (!teamDashboardContext) return null;
    if (sessionInfo?.team_id) {
      return getActiveTeamDashboardContext(teamDashboardContext, sessionInfo.team_id);
    }
    return teamDashboardContext;
  }, [teamDashboardContext, sessionInfo?.team_id]);

  useEffect(() => {
    if (!uploadBatch || !effectiveSessionId || !userId) return;
    if (!getActiveUploadBatch(uploadBatch, effectiveSessionId)) {
      clearUploadBatch(userId);
    }
  }, [uploadBatch, effectiveSessionId, userId]);

  useEffect(() => {
    if (!teamDashboardContext || !sessionInfo || !userId) return;
    if (!sessionInfo.team_id || sessionInfo.team_id !== teamDashboardContext.teamId) {
      clearTeamDashboardContext(userId);
    }
  }, [teamDashboardContext, sessionInfo, userId]);

  useEffect(() => {
    if (!uploadBatch?.createdAt) return;
    if (dismissedBatchAt != null && dismissedBatchAt !== uploadBatch.createdAt) {
      setDismissedBatchAt(null);
    }
  }, [uploadBatch?.createdAt, dismissedBatchAt]);

  // Show welcome modal immediately when no user sessions exist
  useEffect(() => {
    // Don't show modal while sessions are still loading
    if (!sessions) {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // Create user-specific localStorage key
    const storageKey = user?.id ? `glyyd-user-choice-${user.id}` : 'glyyd-user-choice';

    // Check if user has explicitly chosen demo
    const hasChosenDemo = localStorage.getItem(storageKey) === 'demo';

    // Check if user has explicitly chosen upload (has real sessions)
    const hasChosenUpload = localStorage.getItem(storageKey) === 'upload';

    // Check if user has dismissed the modal
    const hasSeenModal = localStorage.getItem(storageKey) === 'seen';

    if (hasChosenDemo) {
      setShowWelcomeModal(false);
    } else if (hasChosenUpload) {
      // User has previously uploaded sessions - hide modal
      setShowWelcomeModal(false);
    } else if (hasSeenModal) {
      // User has dismissed the modal - don't show again
      setShowWelcomeModal(false);
    } else if (sessions.length > 0) {
      // Check if user has uploaded a real session (not demo) - use the memoized value
      if (hasUserSessions) {
        // User has uploaded a session - hide modal permanently
        localStorage.setItem(storageKey, 'upload');
        setShowWelcomeModal(false);
      } else {
        // Only demo sessions exist - show modal
        setShowWelcomeModal(true);
      }
    } else {
      // No sessions at all - show modal
      setShowWelcomeModal(true);
    }
  }, [sessions, user?.id, hasUserSessions]);

  // Prepare session data for overview cards
  const sessionData = {
    averagePower: (averagePowerError ? null : averagePowerData?.crewAverage) || 0,
    strokeRate: strokeRate, // Calculated from telemetry data
    totalDistance:
      comprehensiveData?.gpsData?.length > 0
        ? comprehensiveData.gpsData[comprehensiveData.gpsData.length - 1].distance
        : 0,
    efficiency: efficiency, // Calculated from telemetry data
    powerTrend: 0, // TODO: Calculate trend
    strokeRateTrend: 0, // TODO: Calculate trend
    distanceTrend: 0, // TODO: Calculate trend
    efficiencyTrend: 0, // TODO: Calculate trend
  };

  // Get enhanced GPS data safely - only use real data from database
  const enhancedGPSData = comprehensiveData?.gpsData || [];
  const gpsStats = { totalPoints: enhancedGPSData.length };

  // Prepare session info for CompactHeader
  const compactSessionInfo = {
    id: sessionId || undefined,
    sessionId: sessionInfo?.sessionId || '', // legacy/import label
    sessionName: sessionInfo?.sessionName || 'No Session Selected',
    location: sessionInfo?.crewName || 'Upload a session to view data', // Using crewName as a placeholder for location
    sessionDate: sessionInfo?.sessionDate || '',
    startTime: sessionInfo?.startTime || '',
    boatType: sessionInfo?.boatType || '',
    crewCount: undefined, // Don't show crew count when no session is selected
  };

  const headerActions = {
    onShare: () => {
      // Share functionality to be implemented
    },
    onExport: () => {
      // Export functionality to be implemented
    },
    onSettings: () => {
      // Settings functionality to be implemented
    },
  };

  // Welcome modal handlers
  const handleWelcomeModalClose = () => {
    const storageKey = user?.id ? `glyyd-user-choice-${user.id}` : 'glyyd-user-choice';
    // Mark that user has seen the modal (they can still upload later)
    localStorage.setItem(storageKey, 'seen');
    setShowWelcomeModal(false);
  };
  const handleWelcomeModalUpload = () => {
    // Don't set user choice yet - they might not complete the upload
    setShowWelcomeModal(false);
    navigate('/coach/upload');
  };
  const handleWelcomeModalDemo = () => {
    const storageKey = user?.id ? `glyyd-user-choice-${user.id}` : 'glyyd-user-choice';
    localStorage.setItem(storageKey, 'demo');
    setShowWelcomeModal(false);
    // Demo is already loaded, just close modal
  };

  const updateDashboardHash = (sectionHash: DashboardHashTarget) => {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: `#${sectionHash}`,
      },
      { replace: false }
    );
  };

  // Keep compatibility for existing hash links while using one-page overview layout
  useEffect(() => {
    if (!location.hash) return;

    const targetId = mapHashToSectionId(location.hash);
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    const getScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const styles = window.getComputedStyle(parent);
        const canScrollY =
          (styles.overflowY === 'auto' || styles.overflowY === 'scroll') &&
          parent.scrollHeight > parent.clientHeight;
        if (canScrollY) return parent;
        parent = parent.parentElement;
      }
      return null;
    };

    const getScrollRoot = (): HTMLElement | null => {
      return (
        document.getElementById('dashboard-main-scroll') ||
        document.getElementById('app-main-scroll') ||
        getScrollableParent(target)
      );
    };

    const scrollToTarget = (scrollRoot: HTMLElement | null) => {
      if (!scrollRoot) {
        target.scrollIntoView({ behavior, block: 'start' });
        return true;
      }

      const targetRect = target.getBoundingClientRect();
      const rootRect = scrollRoot.getBoundingClientRect();
      const top = Math.max(targetRect.top - rootRect.top + scrollRoot.scrollTop - 12, 0);

      scrollRoot.scrollTo({ top, behavior });
      return true;
    };

    let retryTimer: number | null = null;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const tryScroll = () => {
      if (cancelled) return;
      attempts += 1;

      const scrollRoot = getScrollRoot();
      const didScroll = scrollToTarget(scrollRoot);
      const targetRect = target.getBoundingClientRect();
      const rootTop = scrollRoot ? scrollRoot.getBoundingClientRect().top : 0;
      const isNearTop = Math.abs(targetRect.top - rootTop) < 24;

      if (didScroll && isNearTop) return;
      if (attempts >= maxAttempts) return;
      retryTimer = window.setTimeout(tryScroll, 150);
    };

    const rafId = window.requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const getScrollRoot = (): HTMLElement | null => {
      return (document.getElementById('dashboard-main-scroll') ||
        document.getElementById('app-main-scroll') ||
        document.scrollingElement ||
        document.documentElement) as HTMLElement | null;
    };

    const scrollRoot = getScrollRoot();
    if (!scrollRoot) return;

    const onScroll = () => {
      setShowBackToTop(scrollRoot.scrollTop > 480);
    };

    onScroll();
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname, location.search]);

  const handleBackToTop = () => {
    const scrollRoot =
      document.getElementById('dashboard-main-scroll') ||
      document.getElementById('app-main-scroll') ||
      document.scrollingElement ||
      document.documentElement;
    if (!scrollRoot) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollRoot.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Compact Session Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        {(activeTeamDashboard || activeUploadBatch) && (
          <CoachDashboardNavBar
            teamContext={activeTeamDashboard}
            uploadBatch={activeUploadBatch}
            currentSessionId={effectiveSessionId}
            userId={userId}
            onDismissUploadBatch={() => {
              if (activeUploadBatch) {
                setDismissedBatchAt(activeUploadBatch.createdAt);
              }
            }}
          />
        )}
        <CompactHeader
          sessionInfo={compactSessionInfo}
          actions={headerActions}
          sectionLinksHint="Starter Dashboard combines charts, cards, and session map. Use Create New to build your own dashboard."
          sectionLinks={[
            { label: 'Overview', hash: 'overview' },
            { label: 'Power', hash: 'power' },
            { label: 'Rower Power', hash: 'individual-rower-power' },
            { label: 'Force', hash: 'force' },
            { label: 'Arc', hash: 'arc' },
            { label: 'Map', hash: 'map' },
          ]}
          onSelectSection={(hash) => {
            if (isDashboardHashTarget(hash)) {
              if (hash === 'overview') {
                setOverviewExpandSignal((prev) => prev + 1);
              }
              updateDashboardHash(hash);
            }
          }}
          onSelectSession={(newSessionId) => {
            if (uploadBatch && getActiveUploadBatch(uploadBatch, newSessionId) && userId) {
              navigateToBatchSession(navigate, newSessionId, uploadBatch, userId);
              return;
            }
            if (userId) clearUploadBatch(userId);
            setDismissedBatchAt(null);
            navigate(`/coach?session=${newSessionId}`);
          }}
        />
      </motion.div>

      {/* Main Dashboard Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-6"
        data-testid="main-dashboard-content"
      >
        {sessionId ? (
          <HullStabilityFilterProvider sessionId={sessionId}>
            <div id="dashboard-overview" className="space-y-4">
              <div className="space-y-4">
                <SessionRosterPanel
                  sessionId={sessionId}
                  expandSignal={overviewExpandSignal > 0 ? overviewExpandSignal : undefined}
                />
                <SessionParametersPanel
                  parameters={parameterData}
                  loading={comprehensiveLoading}
                  sessionId={sessionId}
                  expandSignal={overviewExpandSignal > 0 ? overviewExpandSignal : undefined}
                />
                <HullStabilityPanel sessionId={sessionId} />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="my-6"
              >
                <OverviewCards
                  powerData={transformedPowerData}
                  gpsData={enhancedGPSData}
                  sessionData={sessionData}
                  loading={false}
                  strokeRateLoading={strokeRateLoading}
                  efficiencyLoading={efficiencyLoading}
                  powerLoading={averagePowerLoading}
                  gpsLoading={comprehensiveLoading}
                />
              </motion.div>

              <div id="dashboard-power" className="h-[500px] sm:h-[600px] lg:h-[700px]">
                <Suspense fallback={<ChartSkeleton />}>
                  <GraphWidget
                    graphId="power-analysis"
                    sessionId={sessionId}
                    parameterInfo={parameterData}
                  />
                </Suspense>
              </div>

              <OverviewMetricCardsWithHullFilter
                data={averagePowerError ? null : averagePowerData}
                loading={averagePowerLoading}
                hasError={!!averagePowerError}
              />
            </div>

            <div
              id="dashboard-analysis"
              data-testid="dashboard-analysis-section"
              className="space-y-6"
            >
              <div
                id="dashboard-individual-rower-power"
                className="h-[680px] sm:h-[700px] lg:h-[680px] xl:h-[540px]"
              >
                <Suspense fallback={<ChartSkeleton />}>
                  <GraphWidget
                    graphId="performance-metrics"
                    sessionId={sessionId}
                    parameterInfo={parameterData}
                  />
                </Suspense>
              </div>

              <div id="dashboard-gate-force" className="h-[620px] sm:h-[700px] lg:h-[820px]">
                <Suspense fallback={<ChartSkeleton />}>
                  <GraphWidget
                    graphId="gate-force"
                    sessionId={sessionId}
                    parameterInfo={parameterData}
                  />
                </Suspense>
              </div>

              <div id="dashboard-rowing-arc" className="w-full min-w-0">
                <Suspense fallback={<ChartSkeleton />}>
                  <GraphWidget
                    graphId="rowing-arc"
                    sessionId={sessionId}
                    parameterInfo={parameterData}
                    tileMode={false}
                  />
                </Suspense>
              </div>
            </div>
          </HullStabilityFilterProvider>
        ) : (
          <>
            <div id="dashboard-overview" className="space-y-4">
              <div className="space-y-4">
                <SessionRosterPanel
                  sessionId={sessionId}
                  expandSignal={overviewExpandSignal > 0 ? overviewExpandSignal : undefined}
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-blue-800">
                    Please select a session to view session parameters
                  </p>
                </div>
              </div>
            </div>

            <div
              id="dashboard-analysis"
              data-testid="dashboard-analysis-section"
              className="space-y-6"
            >
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-blue-800">
                  Select a session to load Power Analysis and D3 charts
                </p>
              </div>
            </div>
          </>
        )}

        <div id="dashboard-map" data-testid="dashboard-map-section">
          <ChartContainer
            title="Session GPS Track"
            subtitle={
              gpsStats.totalPoints > 0
                ? `Complete GPS track: ${gpsStats.totalPoints} data points`
                : 'GPS track will be displayed here once uploaded'
            }
            loading={comprehensiveLoading}
            maximizable={true}
          >
            <ComponentErrorBoundary
              fallback={<div className="p-4 text-center text-gray-500">GPS map unavailable</div>}
            >
              <Suspense fallback={<ChartSkeleton />}>
                <GPSTrackMap data={enhancedGPSData} aperiodicGPS={[]} title="" subtitle="" />
              </Suspense>
            </ComponentErrorBoundary>
          </ChartContainer>
        </div>
      </motion.div>

      {/* Welcome Modal Overlay */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        onUpload={handleWelcomeModalUpload}
        onDemo={handleWelcomeModalDemo}
        hasDemoSession={sessions && sessions.some((s) => s.isDemo)}
        isLoading={sessionsLoading}
      />

      {showBackToTop && (
        <button
          type="button"
          onClick={handleBackToTop}
          className="fixed right-6 bottom-24 z-40 h-11 w-11 rounded-full bg-accent text-accent-foreground shadow-lg hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-accent/50"
          aria-label="Back to top"
          title="Back to top"
        >
          <ArrowUp className="h-5 w-5 mx-auto" />
        </button>
      )}
    </motion.div>
  );
};

export default CoachDashboard;
